import { z } from 'zod';

import { IsoDateTimeSchema, UuidSchema, BooleanQueryParamSchema } from './common.js';
import { PaginationQuerySchema, paginatedResponseSchema } from './pagination.js';

/**
 * Cruise master (Cruise-as-stay phase 1).
 *
 * A separate accommodation entity from Hotel - cruises sell cabins (not
 * rooms) and price per-sailing, but the master/cabin/rate shape mirrors the
 * B4.7 preferred-hotel pattern (Hotel + HotelRoom + HotelInventory). There is
 * no TBO mirror for cruises, so the TBO-mapping / supplier-mapping / legacy
 * ETL fields that Hotel carries do not apply here.
 */

// ---------------------------------------------------------------------------
// Enums
// ---------------------------------------------------------------------------

export const CruiseStatusSchema = z.enum([
  'DRAFT',
  'PENDING_APPROVAL',
  'ACTIVE',
  'INACTIVE',
  'ARCHIVED',
]);
export type CruiseStatus = z.infer<typeof CruiseStatusSchema>;

// ---------------------------------------------------------------------------
// CruisePhoto + amenity JSON shapes (validated as `unknown[]` on the wire to
// keep the JSON column tolerant of legacy variants; the editor enforces the
// shape client-side via the typed helpers below).
// ---------------------------------------------------------------------------

export const CruisePhotoSchema = z
  .object({
    /** Media-library asset id (preferred - survives presigned-URL expiry). */
    mediaId: z.string().uuid().optional(),
    /** Direct URL. Legacy imports only; new uploads store mediaId. */
    url: z.string().url('Photo URL must be a valid URL').optional(),
    caption: z.string().max(200).nullable().optional(),
    alt: z.string().max(200).nullable().optional(),
    sortOrder: z.number().int().min(0).optional(),
  })
  .refine((p) => Boolean(p.mediaId || p.url), {
    message: 'Photo needs a mediaId or a url',
  });
export type CruisePhoto = z.infer<typeof CruisePhotoSchema>;

export const CruisePhotoListSchema = z.array(CruisePhotoSchema);
export const CruiseAmenityListSchema = z.array(z.string().trim().min(1).max(80));

// ---------------------------------------------------------------------------
// CruiseCabin (child of Cruise)
// ---------------------------------------------------------------------------

// Meal options + extra-bed pricing live on the cabin rate (CruiseCabinRate),
// not the cabin - they can vary by sailing/season. The cabin is identity +
// occupancy + deck only.

export const CruiseCabinSchema = z.object({
  id: UuidSchema,
  cruiseId: UuidSchema,
  name: z.string(),
  roomTypeId: UuidSchema.nullable(),
  roomTypeName: z.string().nullable(),
  occupancyTypeId: UuidSchema.nullable(),
  occupancyTypeName: z.string().nullable(),
  mealPlanId: UuidSchema.nullable(),
  mealPlanCode: z.string().nullable(),
  /** Cruise-specific deck reference (Upper/Lower/Deck 8/...). */
  deck: z.string().nullable(),
  description: z.string().nullable(),
  photos: z.unknown().nullable(),
  minOccupancy: z.number().int(),
  /** Max bed-needing occupants. `max == base` = strict (no extra bed); `max >
   *  base` allows `max - base` extra beds. Base occupancy lives on the rate card. */
  maxOccupancy: z.number().int(),
  cabinInventoryCount: z.number().int(),
  extraBedChildPrice: z.number().nullable(),
  isActive: z.boolean(),
  sortOrder: z.number().int(),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
});
export type CruiseCabin = z.infer<typeof CruiseCabinSchema>;

const CruiseCabinCoreFieldsSchema = z.object({
  name: z.string().trim().min(1, 'Cabin name is required').max(200),
  roomTypeId: UuidSchema.nullable().optional(),
  occupancyTypeId: UuidSchema.nullable().optional(),
  mealPlanId: UuidSchema.nullable().optional(),
  deck: z.string().trim().max(80).nullable().optional(),
  description: z.string().max(5000).nullable().optional(),
  photos: CruisePhotoListSchema.nullable().optional(),
  minOccupancy: z.number().int().min(1).max(20).optional(),
  maxOccupancy: z.number().int().min(1).max(20).optional(),
  cabinInventoryCount: z.number().int().min(0).max(10_000).optional(),
  extraBedChildPrice: z.number().nonnegative().nullable().optional(),
  isActive: z.boolean().optional(),
  sortOrder: z.number().int().min(0).optional(),
});

/** `maxOccupancy` must be >= `minOccupancy`. Only enforced when both bounds
 *  are present (an update may send neither / one). */
const occupancyOrdered = (v: { minOccupancy?: number; maxOccupancy?: number }): boolean =>
  v.minOccupancy === undefined || v.maxOccupancy === undefined || v.maxOccupancy >= v.minOccupancy;
const occupancyOrderedError = {
  message: 'Max occupancy must be >= min occupancy',
  path: ['maxOccupancy'],
};

export const CreateCruiseCabinRequestSchema = CruiseCabinCoreFieldsSchema.refine(
  occupancyOrdered,
  occupancyOrderedError,
);
export type CreateCruiseCabinRequest = z.infer<typeof CreateCruiseCabinRequestSchema>;

export const UpdateCruiseCabinRequestSchema = CruiseCabinCoreFieldsSchema.partial().refine(
  occupancyOrdered,
  occupancyOrderedError,
);
export type UpdateCruiseCabinRequest = z.infer<typeof UpdateCruiseCabinRequestSchema>;

// ---------------------------------------------------------------------------
// Cruise (master)
// ---------------------------------------------------------------------------

export const CruiseListItemSchema = z.object({
  id: UuidSchema,
  name: z.string(),
  slug: z.string().nullable(),
  starRating: z.number().int(),
  cruiseLine: z.string().nullable(),
  countryId: UuidSchema.nullable(),
  countryName: z.string().nullable(),
  cityId: UuidSchema.nullable(),
  cityName: z.string().nullable(),
  vendorSupplierId: UuidSchema.nullable(),
  vendorSupplierName: z.string().nullable(),
  status: CruiseStatusSchema,
  isApprovedByAdmin: z.boolean(),
  cabinCount: z.number().int(),
  /** Raw photo JSON (cover = sortOrder 0) so list/picker cards can show a
   *  cover thumbnail; validated loosely, resolved to a URL on the client. */
  photos: z.unknown().nullable(),
  /** Lowest adult fare across the cruise's active rate cards ("from" price),
   *  in `fromPriceCurrency`. Null when the cruise has no active rates. */
  fromPrice: z.number().nullable(),
  fromPriceCurrency: z.string().nullable(),
  createdAt: IsoDateTimeSchema,
});
export type CruiseListItem = z.infer<typeof CruiseListItemSchema>;

export const CruiseListQuerySchema = PaginationQuerySchema.extend({
  status: CruiseStatusSchema.optional(),
  cityId: UuidSchema.optional(),
  countryId: UuidSchema.optional(),
  vendorSupplierId: UuidSchema.optional(),
  starRating: z.coerce.number().int().min(1).max(5).optional(),
  isApprovedByAdmin: BooleanQueryParamSchema.optional(),
  includeDeleted: BooleanQueryParamSchema.optional(),
});
export type CruiseListQuery = z.infer<typeof CruiseListQuerySchema>;

export const CruiseListResponseSchema = paginatedResponseSchema(CruiseListItemSchema);
export type CruiseListResponse = z.infer<typeof CruiseListResponseSchema>;

export const CruiseDetailSchema = CruiseListItemSchema.extend({
  address: z.string().nullable(),
  latitude: z.number().nullable(),
  longitude: z.number().nullable(),
  shortDescription: z.string().nullable(),
  longDescriptionHtml: z.string().nullable(),
  amenities: z.unknown().nullable(),
  photos: z.unknown().nullable(),
  /** Sailing check-in / check-out times (HH:mm), the cruise analogue of a
   *  hotel's checkIn/checkOut times. */
  embarkTime: z.string().nullable(),
  disembarkTime: z.string().nullable(),
  policiesHtml: z.string().nullable(),
  contractRef: z.string().nullable(),
  markupKind: z.enum(['PERCENT', 'FLAT']).nullable(),
  markupValue: z.number(),
  lastApprovedAt: IsoDateTimeSchema.nullable(),
  lastApprovedById: UuidSchema.nullable(),
  cabins: z.array(CruiseCabinSchema),
  updatedAt: IsoDateTimeSchema,
  deletedAt: IsoDateTimeSchema.nullable(),
});
export type CruiseDetail = z.infer<typeof CruiseDetailSchema>;

const CruiseCoreFieldsSchema = z.object({
  // Identity
  name: z.string().trim().min(1, 'Cruise name is required').max(300),
  slug: z.string().trim().min(1).max(160).nullable().optional(),
  starRating: z.number().int().min(0).max(5).optional(),
  cruiseLine: z.string().trim().max(120).nullable().optional(),
  // Location
  countryId: UuidSchema.nullable().optional(),
  cityId: UuidSchema.nullable().optional(),
  address: z.string().trim().max(500).nullable().optional(),
  latitude: z.number().min(-90).max(90).nullable().optional(),
  longitude: z.number().min(-180).max(180).nullable().optional(),
  // Content
  shortDescription: z.string().max(1000).nullable().optional(),
  longDescriptionHtml: z.string().max(50_000).nullable().optional(),
  amenities: CruiseAmenityListSchema.nullable().optional(),
  photos: CruisePhotoListSchema.nullable().optional(),
  embarkTime: z
    .string()
    .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Use HH:mm format')
    .nullable()
    .optional(),
  disembarkTime: z
    .string()
    .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Use HH:mm format')
    .nullable()
    .optional(),
  policiesHtml: z.string().max(20_000).nullable().optional(),
  // Supplier / commercial
  vendorSupplierId: UuidSchema.nullable().optional(),
  contractRef: z.string().trim().max(120).nullable().optional(),
  markupKind: z.enum(['PERCENT', 'FLAT']).nullable().optional(),
  markupValue: z.number().nonnegative().optional(),
  // Approval workflow
  status: CruiseStatusSchema.optional(),
});

export const CreateCruiseRequestSchema = CruiseCoreFieldsSchema;
export type CreateCruiseRequest = z.infer<typeof CreateCruiseRequestSchema>;

export const UpdateCruiseRequestSchema = CruiseCoreFieldsSchema.partial();
export type UpdateCruiseRequest = z.infer<typeof UpdateCruiseRequestSchema>;

/** Approval action (admin signs off). Mirrors the master approval pattern. */
export const CruiseApprovalRequestSchema = z.object({
  approve: z.boolean(),
});
export type CruiseApprovalRequest = z.infer<typeof CruiseApprovalRequestSchema>;

// ---------------------------------------------------------------------------
// Re-export the cabin rate schemas so consumers can import everything cruise
// related from a single module (`./cruises`), mirroring how Hotel + HotelRoom
// live in `hotels.ts` while HotelInventory has its own file.
// ---------------------------------------------------------------------------

export {
  CruiseCabinRateMealOptionSchema,
  CruiseCabinRateMealOptionInputSchema,
  CruiseCabinRateSchema,
  CruiseCabinRateListQuerySchema,
  CruiseCabinRateListResponseSchema,
  CruiseCabinRateInputSchema,
  CreateCruiseCabinRateRequestSchema,
  UpdateCruiseCabinRateRequestSchema,
  type CruiseCabinRateMealOption,
  type CruiseCabinRateMealOptionInput,
  type CruiseCabinRate,
  type CruiseCabinRateListQuery,
  type CruiseCabinRateListResponse,
  type CruiseCabinRateInput,
  type CreateCruiseCabinRateRequest,
  type UpdateCruiseCabinRateRequest,
} from './cruise-rates.js';
